import { useMemo } from "react";
import AssigneesAvatarGroup, {
  type AssigneeAvatarItem,
} from "components/common/AssigneesAvatarGroup";
import type {
  WorkOrder,
  WorkOrderAssignmentSummary,
} from "features/WorkOrders/types/work-order.types";

function assigneeLabel(a: WorkOrderAssignmentSummary): string {
  const u = a.user;
  const name = [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (u?.email) return u.email;
  const id = u?.id ?? a.toUserId;
  return id > 0 ? `User #${id}` : "—";
}

function assigneeUserId(a: WorkOrderAssignmentSummary): number | null {
  const raw = a.user?.id ?? a.toUserId;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function dedupeKey(a: WorkOrderAssignmentSummary): number {
  const uid = assigneeUserId(a);
  if (uid != null) return uid;
  const id = Number(a.id);
  return Number.isFinite(id) && id > 0 ? id : 0;
}

export default function WorkOrderAssigneesAvatarGroup({
  workOrder,
  workAssignments,
  canReadDetail,
  canAssignMore,
  onOpenAssign,
}: {
  workOrder: WorkOrder;
  workAssignments?: WorkOrderAssignmentSummary[] | null;
  canReadDetail: boolean;
  canAssignMore: boolean;
  onOpenAssign: (wo: WorkOrder) => void;
}) {
  const assignees = useMemo<AssigneeAvatarItem[]>(() => {
    const list = (workAssignments ?? []).filter((a) => !a.deleted_at);
    const map = new Map<number, WorkOrderAssignmentSummary>();
    for (const a of list) {
      const k = dedupeKey(a);
      if (k > 0 && !map.has(k)) map.set(k, a);
    }
    return Array.from(map.values()).map((a) => ({
      key: dedupeKey(a) || a.id,
      userId: assigneeUserId(a),
      label: assigneeLabel(a),
      avatar: a.user?.avatar,
      employee: a.user?.employee
        ? { id: a.user.employee.id, code: a.user.employee.code ?? "" }
        : undefined,
    }));
  }, [workAssignments]);

  return (
    <AssigneesAvatarGroup
      assignees={assignees}
      detailTo={`/work-orders/${workOrder.id}`}
      canReadDetail={canReadDetail}
      canAssignMore={canAssignMore}
      onOpenAssign={() => onOpenAssign(workOrder)}
      emptyTitle="No assignees — open record to assign"
      addTitle="Assign user"
      showAddWhenLessThan={5}
    />
  );
}
